/** Audience values validated by API — hard-coded labels only. */
export const AUDIENCE_OPTIONS = [
  { value: 'corporate_only', label: 'Corporate policies only', description: 'Employer-sponsored programs (default).' },
  { value: 'all_enrollees', label: 'All enrollee types', description: 'Rare; when program is not corporate-specific.' },
];

/** Product copy — HR roster always starts here for corporate programmes. */
export const CORPORATE_ROSTER_BASIS =
  'Active policies on the organization (members the employer has paid for). Inactive policies are never included.';

export const ELIGIBILITY_STACK_NOTE =
  'Eligibility rules below narrow that paid roster — they do not add people from inactive policies.';

/** Quick-pick templates (§5 of eligibility guide). */
export const ELIGIBILITY_PRESETS = [
  {
    key: 'annual_medicals',
    label: 'Annual medicals (employer)',
    patch: { member_scope: 'active_principals', genders: [], age_min: null, age_max: null },
  },
  {
    key: 'female_wellness',
    label: 'Female wellness (staff)',
    patch: { member_scope: 'active_principals', genders: ['female'], age_min: 18, age_max: null },
  },
  {
    key: 'family_wellness',
    label: 'Family wellness day',
    patch: { member_scope: 'active_members', genders: [], age_min: null, age_max: null },
  },
  {
    key: 'pediatric_vaccine',
    label: 'Pediatric vaccine (dependents)',
    patch: { member_scope: 'active_dependents', genders: [], age_min: null, age_max: 12 },
  },
  {
    key: 'girls_adolescent',
    label: 'Girls adolescent program',
    patch: { member_scope: 'active_dependents', genders: ['female'], age_min: 10, age_max: 17 },
  },
  {
    key: 'open_corporate',
    label: 'Open corporate benefit',
    patch: { member_scope: 'all_members', genders: [], age_min: null, age_max: null },
  },
];

function normalizeOptionEntry(raw) {
  if (typeof raw === 'string') {
    return { value: raw, label: raw.replace(/_/g, ' '), description: '' };
  }
  if (!raw || typeof raw !== 'object') return null;
  const value = raw.value ?? raw.id;
  if (!value) return null;
  return {
    value: String(value),
    label: raw.label ?? String(value).replace(/_/g, ' '),
    description: raw.description ?? raw.help ?? '',
  };
}

/** Member scope options — schema only (no invented values). */
export function resolveMemberScopeOptions(schema) {
  const list = schema?.eligibility?.member_scope_options
    ?? schema?.defaults?.eligibility?.member_scope_options;
  if (!Array.isArray(list)) return [];
  return list.map(normalizeOptionEntry).filter(Boolean);
}

/** Gender options — schema only. */
export function resolveGenderOptions(schema) {
  const list = schema?.eligibility?.gender_options;
  if (!Array.isArray(list)) return [];
  return list.map(normalizeOptionEntry).filter(Boolean);
}

/** Age input bounds from schema `age_bounds` or guide defaults. */
export function resolveAgeBounds(schema) {
  const bounds = schema?.eligibility?.age_bounds ?? {};
  const minMeta = bounds.age_min ?? {};
  const maxMeta = bounds.age_max ?? {};
  return {
    min: Number.isFinite(minMeta.min) ? minMeta.min : 0,
    max: Number.isFinite(minMeta.max) ? minMeta.max : 150,
  };
}

/** Migrate legacy `filters` / single gender / audience `all` into guide shape. */
export function normalizeEligibilityRaw(raw = {}) {
  let genders = Array.isArray(raw.genders)
    ? raw.genders.filter((g) => g === 'male' || g === 'female')
    : [];

  if (!genders.length) {
    const legacyGender = raw.filters?.gender ?? raw.filter?.gender;
    if (legacyGender === 'male' || legacyGender === 'female') {
      genders = [legacyGender];
    }
  }

  // Both selected = no gender restriction (same as empty per guide)
  if (genders.length >= 2) genders = [];

  let audience = raw.audience;
  if (audience === 'all') audience = 'all_enrollees';
  if (!audience) audience = 'corporate_only';

  const parseAge = (v) => {
    if (v === '' || v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  let ageMin = parseAge(raw.age_min);
  let ageMax = parseAge(raw.age_max);
  if (ageMin == null && raw.filters?.age_min != null) ageMin = parseAge(raw.filters.age_min);
  if (ageMax == null && raw.filters?.age_max != null) ageMax = parseAge(raw.filters.age_max);

  return {
    audience,
    require_org_enabled: raw.require_org_enabled !== false,
    member_scope: raw.member_scope ?? 'all_members',
    genders,
    age_min: ageMin,
    age_max: ageMax,
  };
}

/** Payload shape for PUT config.eligibility. */
export function sanitizeEligibilityForSave(raw = {}) {
  const normalized = normalizeEligibilityRaw(raw);
  return {
    audience: normalized.audience,
    require_org_enabled: normalized.require_org_enabled,
    member_scope: normalized.member_scope,
    genders: normalized.genders,
    age_min: normalized.age_min,
    age_max: normalized.age_max,
  };
}

export function validateEligibility(eligibility = {}, schema = null) {
  const errors = [];
  const e = normalizeEligibilityRaw(eligibility);

  if (!AUDIENCE_OPTIONS.some((o) => o.value === e.audience)) {
    errors.push('Audience must be corporate_only or all_enrollees');
  }

  if (!e.member_scope) {
    errors.push('Member scope is required');
  } else {
    const scopeOptions = resolveMemberScopeOptions(schema);
    if (scopeOptions.length > 0 && !scopeOptions.some((o) => o.value === e.member_scope)) {
      errors.push('Member scope is invalid — choose a value from the schema');
    }
  }

  e.genders.forEach((g, i) => {
    if (g !== 'male' && g !== 'female') {
      errors.push(`Gender [${i}] must be male or female`);
    }
  });

  const { min: ageFloor, max: ageCeil } = resolveAgeBounds(schema);
  if (e.age_min != null && (e.age_min < ageFloor || e.age_min > ageCeil)) {
    errors.push(`Minimum age must be between ${ageFloor} and ${ageCeil}`);
  }
  if (e.age_max != null && (e.age_max < ageFloor || e.age_max > ageCeil)) {
    errors.push(`Maximum age must be between ${ageFloor} and ${ageCeil}`);
  }
  if (e.age_min != null && e.age_max != null && e.age_min > e.age_max) {
    errors.push('Minimum age cannot be greater than maximum age');
  }

  return errors;
}

function genderSummary(genders, genderOptions) {
  if (!genders?.length || genders.length >= 2) return null;
  const token = genders[0];
  const match = genderOptions.find((o) => o.value === token);
  const label = match?.label ?? (token === 'female' ? 'Female' : token === 'male' ? 'Male' : token);
  return `${label} only`;
}

function ageSummary(ageMin, ageMax) {
  if (ageMin != null && ageMax != null) return `age ${ageMin}–${ageMax}`;
  if (ageMin != null) return `age ${ageMin}+`;
  if (ageMax != null) return `age up to ${ageMax}`;
  return null;
}

/** One-line member filter summary for review cards. */
export function describeEligibilityMembers(eligibility = {}, { memberScopeOptions = [], genderOptions = [] } = {}) {
  const e = normalizeEligibilityRaw(eligibility);
  const parts = [];

  const scope = memberScopeOptions.find((o) => o.value === e.member_scope);
  parts.push(scope?.label ?? e.member_scope?.replace(/_/g, ' ') ?? '—');

  const genderPart = genderSummary(e.genders, genderOptions);
  if (genderPart) parts.push(genderPart);

  const agePart = ageSummary(e.age_min, e.age_max);
  if (agePart) parts.push(agePart);

  return parts.join('; ');
}

/** Full summary for publish review step. */
export function describeEligibilityFull(eligibility = {}, options = {}) {
  const e = normalizeEligibilityRaw(eligibility);
  const audienceLabel = AUDIENCE_OPTIONS.find((o) => o.value === e.audience)?.label
    ?? e.audience;
  const isCorporate = e.audience !== 'all_enrollees';
  return {
    rosterBasis: isCorporate ? CORPORATE_ROSTER_BASIS : 'All enrollee types per audience setting.',
    members: describeEligibilityMembers(e, options),
    audience: audienceLabel,
    orgRequired: e.require_org_enabled !== false ? 'Yes' : 'No',
  };
}

export function lookupMemberScopeLabel(scopeValue, memberScopeOptions = []) {
  if (!scopeValue) return '—';
  return memberScopeOptions.find((o) => o.value === scopeValue)?.label
    ?? String(scopeValue).replace(/_/g, ' ');
}
